/*
 * 版本检测工具 - 优化版
 * 不依赖 version.json，直接从 index.html 的 meta 标签读取版本
 */
import Axios from "axios";

const VERSION_CHECK_INTERVAL = 30 * 60 * 1000; // 30分钟检查一次
const LOCAL_VERSION_KEY = "app_version";

class VersionChecker {
  constructor() {
    this.currentVersion = null;
    this.checkTimer = null;
  }

  /**
   * 初始化版本检测
   */
  async init() {
    try {
      // 获取当前页面的版本（从 meta 标签）
      const currentPageVersion = this.getCurrentPageVersion();

      if (!currentPageVersion) {
        console.warn("[版本检测] 无法从当前页面获取版本信息");
        return;
      }

      const localVersion = localStorage.getItem(LOCAL_VERSION_KEY);

      // 首次访问，记录版本
      if (!localVersion) {
        localStorage.setItem(LOCAL_VERSION_KEY, currentPageVersion);
        this.currentVersion = currentPageVersion;
        console.log("[版本检测] 首次访问，当前版本:", currentPageVersion);
      }
      // 版本不一致，说明已经是新版本了（用户可能是强制刷新进来的）
      else if (localVersion !== currentPageVersion) {
        console.log("[版本检测] 检测到新版本已加载:", currentPageVersion, "旧版本:", localVersion);
        // 更新本地版本号
        localStorage.setItem(LOCAL_VERSION_KEY, currentPageVersion);
        this.currentVersion = currentPageVersion;
      } else {
        this.currentVersion = currentPageVersion;
        console.log("[版本检测] 版本一致:", currentPageVersion);
      }

      // 启动定时检测（检查服务器是否有更新）
      this.startPeriodicCheck();
    } catch (error) {
      console.error("[版本检测] 初始化失败:", error);
    }
  }

  /**
   * 从当前页面的 meta 标签获取版本号
   */
  getCurrentPageVersion() {
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag ? metaTag.getAttribute('content') : null;
  }

  /**
   * 从服务器获取最新版本信息（通过请求 index.html 并解析 meta 标签）
   */
  async fetchServerVersion() {
    try {
      // 添加时间戳防止缓存
      const timestamp = Date.now();
      const response = await Axios.get(
        `/?t=${timestamp}`,
        {
          // 不缓存
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache"
          },
          timeout: 5000,
          // 获取完整的 HTML
          responseType: "text"
        }
      );

      if (response.data) {
        // 解析 HTML 中的 meta 标签
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, "text/html");
        const metaTag = doc.querySelector('meta[name="app-version"]');

        if (metaTag) {
          return metaTag.getAttribute('content');
        }
      }
      return null;
    } catch (error) {
      console.error("[版本检测] 获取服务器版本失败:", error);
      return null;
    }
  }

  /**
   * 显示更新提示
   */
  showUpdateNotification(newVersion) {
    // 使用 Mint UI 的 MessageBox
    if (window.MINT && window.MINT.MessageBox) {
      window.MINT.MessageBox({
        title: "发现新版本",
        message: "检测到系统有新版本，请刷新页面以获得最佳体验",
        showCancelButton: true,
        confirmButtonText: "立即刷新",
        cancelButtonText: "稍后"
      }).then(action => {
        if (action === "confirm") {
          // 更新本地版本号
          localStorage.setItem(LOCAL_VERSION_KEY, newVersion);
          // 强制刷新页面（绕过缓存）
          window.location.reload(true);
        }
      });
    } else {
      // 降级方案：使用原生 confirm
      const shouldReload = window.confirm(
        "检测到系统有新版本，是否立即刷新页面？\n（点击确定刷新，点击取消稍后刷新）"
      );
      if (shouldReload) {
        localStorage.setItem(LOCAL_VERSION_KEY, newVersion);
        window.location.reload(true);
      }
    }
  }

  /**
   * 启动定时检测
   */
  startPeriodicCheck() {
    // 清除之前的定时器
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
    }

    // 每隔一段时间检查一次版本
    this.checkTimer = setInterval(async () => {
      try {
        const serverVersion = await this.fetchServerVersion();
        if (serverVersion && serverVersion !== this.currentVersion) {
          console.log("[版本检测] 定时检测发现新版本:", serverVersion, "当前版本:", this.currentVersion);
          // 停止定时检测（避免重复提示）
          this.stop();
          // 显示更新提示
          this.showUpdateNotification(serverVersion);
        }
      } catch (error) {
        console.error("[版本检测] 定时检测失败:", error);
      }
    }, VERSION_CHECK_INTERVAL);
  }

  /**
   * 停止版本检测
   */
  stop() {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
      console.log("[版本检测] 已停止定时检测");
    }
  }
}

// 导出单例
export default new VersionChecker();



// WEBPACK FOOTER //
// ./src/utils/versionCheck.js